import { FC } from 'react'
import { AutopilotCompareTableProps } from './types'
import styles from './AutopilotCompareTable.module.scss'
import { observer } from 'mobx-react'
import { useRootStore } from 'components/_hooks/useRootStore'
import { Img } from 'components/basic/Img'
import { IconMap } from 'components/_const_assets/icons'
import SlideDown from 'components/SlideDown'
import { Icon, IconName } from 'components/Phantom/Icon'

interface ITableItem {
	icon: IconName
	iconAlt: string
	featureName: string
	standardStatus: string
	enchancedStatus: string
}

const TableData: ITableItem[] = [
	{
		icon: 'TemperatureLight',
		iconAlt: 'Temperature icon',
		featureName: 'Automatic temperature',
		standardStatus: IconMap.gray_checkmark,
		enchancedStatus: IconMap.blue_checkmark,
	},
	// {
	// 	icon: IconMap.cool,
	// 	iconAlt: 'Cool icon',
	// 	featureName: 'Scheduled temperature',
	// 	standardStatus: IconMap.gray_checkmark,
	// 	enchancedStatus: IconMap.blue_checkmark,
	// },
	{
		icon: 'VibrationLight',
		iconAlt: 'Vibration icon',
		featureName: 'Vibration and thermal alarm',
		standardStatus: IconMap.gray_checkmark,
		enchancedStatus: IconMap.blue_checkmark,
	},
	{
		icon: 'HeartReportLight',
		iconAlt: 'Heart report icon',
		featureName: 'Sleep and health reports',
		standardStatus: IconMap.gray_checkmark,
		enchancedStatus: IconMap.blue_checkmark,
	},
	// {
	// 	icon: IconMap.price_tag,
	// 	iconAlt: 'Discount icon',
	// 	featureName: 'Discounts on upgrades and accessories',
	// 	standardStatus: IconMap.gray_checkmark,
	// 	enchancedStatus: IconMap.blue_checkmark,
	// },
	{
		icon: 'TwoUsersLight',
		iconAlt: 'Two users icon',
		featureName: 'Two sleep profiles',
		standardStatus: IconMap.gray_checkmark,
		enchancedStatus: IconMap.blue_checkmark,
	},
	{
		icon: 'EightBoxLight',
		iconAlt: 'Eight icon',
		featureName: 'Bi-annual maintenance filter delivery',
		standardStatus: IconMap.gray_checkmark,
		enchancedStatus: IconMap.blue_checkmark,
	},
	{
		icon: 'SnoringLight',
		iconAlt: 'Snoring icon',
		featureName: 'Snoring detection',
		standardStatus: IconMap.gray_checkmark,
		enchancedStatus: IconMap.blue_checkmark,
	},
	{
		icon: 'SnoreMitigationCustom',
		iconAlt: 'Snoring mitigation icon',
		featureName: 'Snoring mitigation',
		standardStatus: IconMap.gray_checkmark,
		enchancedStatus: IconMap.blue_checkmark,
	},
]

const AutopilotFAQ = () => {
	return (
		<div>
			<h3 className={styles.faq_headline}>FAQs</h3>
			<div className={styles.faqs}>
				<SlideDown
					top={<p className={styles.question}>Why does the Pod need Autopilot?</p>}
					className={styles.question_container}
					inverted
				>
					<span className={styles.answer}>
						The Pod needs Autopilot to ensure sleepers experience optimal sleep stages by adjusting bed temperature and elevation throughout the night. This is crucial because a key factor for poor
						sleep is maintaining the wrong temperature. Autopilot aims to match your body’s natural circadian rhythm and temperature needs for each sleep stage, enhancing the quality of deep and REM
						sleep.
					</span>
				</SlideDown>
				<SlideDown
					top={<p className={styles.question}>Can I buy the Pod without Autopilot?</p>}
					className={styles.question_container}
					inverted
				>
					<span className={styles.answer}>Autopilot is required for the first 12 months (cancel any time), and covers two separate users per Pod.</span>
				</SlideDown>
				<SlideDown
					top={<p className={styles.question}>What is the commitment length for Autopilot?</p>}
					className={styles.question_container}
					inverted
				>
					<span className={styles.answer}>Autopilot comes in annual plans and is billed once yearly.</span>
				</SlideDown>
				<SlideDown
					top={<p className={styles.question}>Do I need to get Autopilot for both sides of the bed?</p>}
					className={styles.question_container}
					inverted
				>
					<span className={styles.answer}>If one partner has Autopilot, they can add a partner, for free so they can both enjoy the better sleep that comes with Autopilot.</span>
				</SlideDown>
			</div>
		</div>
	)
}

export const AutopilotBrieferStoreConnected = observer(() => {
	const { settingsStore } = useRootStore()
	return <AutopilotCompareTable />
})

export const AutopilotCompareTable: FC<AutopilotCompareTableProps> = (props) => {
	const { title = 'Put your sleep on Autopilot', showFAQ = true } = props

	return (
		<section className={styles.container}>
			<div className={styles.inner}>
				<header className={styles.header}>
					<h2>{title}</h2>
				</header>

				<div className={styles.table_heading}>
					<span>Standard</span>
					<span>Enhanced</span>
				</div>
				<ul className={styles.table}>
					{TableData.map((item, index) => (
						<li key={index}>
							<span className={styles.icon}>
								<Icon
									name={item.icon}
									color={'black'}
								/>
							</span>
							<h5 className={styles.feature_name}>{item.featureName}</h5>
							<Img
								alt=""
								src={item.standardStatus}
								simple
								lazy={false}
							/>
							<Img
								alt=""
								src={item.enchancedStatus}
								simple
								lazy={false}
							/>
						</li>
					))}
					<li>
						<span className={styles.icon}>
							<Img
								alt={'Shield check icon'}
								src={IconMap.shield_check}
								simple
								lazy={false}
							/>
						</span>
						<h5 className={styles.feature_name}>Extended warranty</h5>
						<span className={styles.feature_status}>2-year</span>
						<span className={styles.feature_status}>5-year</span>
					</li>
				</ul>
				<div className={styles.warranty}>
					<div className={styles.warranty_content}>
						<div className={styles.warranty_logo}>
							<svg
								width="37"
								height="57"
								viewBox="0 0 37 57"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									id="Fill 1"
									fillRule="evenodd"
									clipRule="evenodd"
									d="M26.7637 16.7185C26.7637 18.6412 25.2044 20.1992 23.2818 20.1992H13.5742C11.6517 20.1992 10.0925 18.6412 10.0925 16.7185V12.0421C10.0925 10.1192 11.6517 8.56123 13.5742 8.56123H23.2818C25.2044 8.56123 26.7637 10.1192 26.7637 12.0421V16.7185ZM26.7637 44.2872C26.7637 46.2099 25.2044 47.7681 23.2818 47.7681H13.5742C11.6517 47.7681 10.0925 46.2099 10.0925 44.2872V37.8263C10.0925 35.9036 11.6517 34.3454 13.5742 34.3454H23.2818C25.2044 34.3454 26.7637 35.9036 26.7637 37.8263V44.2872ZM27.2215 0H9.63467C4.31039 0.00240828 0 4.31433 0 9.63905V17.1012C0 21.0259 2.32925 24.3696 5.67173 25.8792V27.2594C2.32925 28.7687 0 32.1329 0 36.036V46.7117C0 52.038 4.31302 56.351 9.63927 56.351H27.2165C32.5429 56.351 36.8562 52.038 36.8562 46.7117V36.036C36.8562 32.1329 34.5269 28.7687 31.1842 27.2594V25.8792C34.5269 24.3696 36.8562 21.0044 36.8562 17.1012V9.63905C36.8562 4.31433 32.5456 0.00240828 27.2215 0Z"
									fill={'white'}
								/>
							</svg>
						</div>
						<div className={styles.warranty_headline}>5-year extended warranty</div>
						<div className={styles.warranty_disclaimer}>*Only included with Enhanced plan. Terms apply</div>
						<div>
							For up to 5 years following your initial purchase date, we will replace or repair your Pod when the damage is the result of a defect in material or workmanship as long as you purchased
							through this site and have the Autopilot Enhanced plan active.
						</div>
					</div>
				</div>
				{showFAQ && <AutopilotFAQ />}
			</div>
		</section>
	)
}
